/**
 * figureFont — 그림 SVG 에 KyMath(Latin Modern Math 부분 추출) 글꼴을 심는다. **표시 직전에만** 심고 저장본에는 넣지 않는다
 * (가이드 docs/figure-svg/BE-20261019-figure-font-embed-guide.md). 백엔드·DB 변경 없음 — 서버 정제기는 <style> 을 지운다.
 *
 * 심는 자리: SvgFigurePreview(resolved) · ImageComponent LazyImage(src) · ImageNode.exportDOM(HTML 내보내기 = 목록 썸네일·
 * 시험지 조립·인쇄 v4·옛 PDF) · figureDownload(PNG 래스터). 편집기 캔버스는 인라인 DOM 이라 ensureFigureFontStyle 로 같은
 * 규칙을 페이지에 건다. 벗기는 자리: ImageNode.importDOM(HTML 붙여넣기) — 심은 src 가 노드에 들어오면 svgPolicy 가 <style>
 * 을 unsupported_style 로 거절해 편집기가 읽기 전용이 된다.
 *
 * 방식: 루트 <svg …> 바로 뒤에 <style> 한 덩이(@font-face + 글꼴 규칙 4줄)를 끼운다. **속성은 손대지 않는다.** 동봉 도우미
 * (docs/figure-svg/font/figureFont.js)는 루트 font-family 를 지우고 serif 로 박아 전체 글꼴을 고딕으로 고른 그림이 명조가 됐다.
 * 규칙 방식은 그 도우미와 픽셀 0 차이(2026-10-19 Chromium, 두 방식을 canvas 에 그려 비교)이고 되돌리기는 그 덩이 제거 하나다.
 * 정규식은 서버가 정제한 SVG(루트 하나·일반 글꼴 이름만·<style> 없음)를 전제로 한다 — 사용자 입력 원문에는 쓰지 않는다.
 *
 * 얼굴(2차, 2026-10-19): 그림은 변수를 font-style="italic" 로 적는데 BE 가 준 KyMath 는 바로 선 글자 한 얼굴뿐이라 브라우저가
 * 합성으로 눕혀 숫자·괄호까지 기울고 x·y 글자꼴이 LaTeX 와 달랐다. 그래서 a-z·A-Z·그리스를 수학 기울임 글리프에 연결한
 * **기울임 얼굴**(kymathFontFaces.js, scripts/build-kymath-faces.py 생성)을 두고, 그림마다 **그 그림이 쓰는 얼굴만** 심는다 —
 * 바로 선 글자는 항상, 기울임은 font-style=italic(·oblique — WebKit 은 합성) 이 있을 때. 브라우저가 @font-face 서술자로 얼굴을 고르므로
 * SVG 는 그대로다. 기울임 얼굴에서 µ(U+00B5)·∂·ℓ 같은 기호는 바로 선 채다(글자 a-z·A-Z·그리스만 눕는다).
 * 굵은 얼굴은 아직 없다(실데이터 0건, 괄호·기호는 가늘게 남아 지금 합성 굵기와 생김새가 달라짐 — 오너 결정 대기). 그동안 굵게는
 * 브라우저 합성 그대로다.
 * 용량(실측): data-URI 가 바로 선 글자만이면 그림당 약 +50KB(438 → 51,342 바이트), 기울임이 있으면 약 +94KB.
 * <img> 마다 독립 문서라 글꼴을 나눠 쓰지 못한다.
 */
import { toDataUri, fromDataUri } from './figureSvg';
import { KYMATH_FAMILY, KYMATH_WOFF2_B64 } from './kymathFont';
import { KYMATH_EXTRA_FACES } from './kymathFontFaces';

/** 얼굴 — Regular(BE 동봉) 뒤에 생성 얼굴. style·weight 는 @font-face 서술자 값이자 facesFor 가 고르는 기준. */
export const KYMATH_FACES = Object.freeze([
  Object.freeze({ name: 'Regular', style: 'normal', weight: 400, b64: KYMATH_WOFF2_B64 }),
  ...KYMATH_EXTRA_FACES,
]);

/**
 * 글꼴 규칙 — <img> 안의 SVG 와 편집기 캔버스가 **같은 표**를 쓴다. 서버가 허용하는 일반 글꼴 3종마다 KyMath 를 앞에 세우고,
 * 루트에 글꼴 지정이 없으면 KyMath, serif. KyMath 에 없는 글자(한글 등)는 뒤의 일반 글꼴로 떨어진다.
 * CSS 선택자는 표현 속성(font-family="…")보다 우선하므로 속성을 바꿀 필요가 없다.
 */
export const FIGURE_FONT_RULES = Object.freeze([
  ['svg:not([font-family])', `font-family:${KYMATH_FAMILY},serif`],
  ['[font-family="serif"]', `font-family:${KYMATH_FAMILY},serif`],
  ['[font-family="sans-serif"]', `font-family:${KYMATH_FAMILY},sans-serif`],
  ['[font-family="monospace"]', `font-family:${KYMATH_FAMILY},monospace`],
]);

/** 얼굴 하나의 @font-face. 서술자(font-style·font-weight)가 있어야 브라우저가 합성 대신 이 얼굴을 고른다. */
export function faceCss(face) {
  return `@font-face{font-family:'${KYMATH_FAMILY}';font-style:${face.style};font-weight:${face.weight};src:url(data:font/woff2;base64,${face.b64}) format('woff2')}`;
}

/** 얼굴 전부의 @font-face — 편집기(<head>)용. 그림에는 facesFor 가 고른 것만 넣는다. */
export const FIGURE_FONT_FACE_CSS = KYMATH_FACES.map(faceCss).join('');

/** 규칙 4줄을 CSS 문자열로. prefix 를 주면 선택자마다 앞에 붙인다(편집기 캔버스: '.svg-editor-mount '). */
export function figureFontCss(prefix = '') {
  return FIGURE_FONT_RULES.map(([selector, declaration]) => `${prefix}${selector}{${declaration}}`).join('');
}

/*
 * 그림이 어떤 글꼴 꼴을 쓰는지 — 속성만 본다(정제된 SVG 는 <style>·style= 이 속성으로 풀려 있다). 어느 요소(text·tspan·g·루트)에
 * 있든, 글자 없는 요소에 있어도 센다 — 과잉 포함은 바이트만 늘 뿐이다. 값은 서버·svgPolicy 가 제한하지 않으므로 넓게 받는다.
 * 기울임: italic|oblique — oblique 는 WebKit 이 Italic 얼굴을 안 고르고 합성한다(보장 못 함, 저장소 표본 0건).
 * 굵기: bold|bolder 또는 숫자 501 이상 — 브라우저는 501 부터 700 얼굴을 고른다(2026-10-19 Chrome·WebKit 실측).
 */
const ITALIC_RE = /font-style\s*=\s*"\s*(italic|oblique)/i;
const WEIGHT_RE = /font-weight\s*=\s*"\s*([^"]*)"/gi;

/** `{ italic, bold }` — 그림 원문이 기울임·굵게를 쓰는가. */
export function fontUseOf(svgText) {
  const text = typeof svgText === 'string' ? svgText : '';
  let bold = false;
  for (const m of text.matchAll(WEIGHT_RE)) {
    const v = m[1].trim().toLowerCase();
    if (v === 'bold' || v === 'bolder' || Number(v) >= 501) { bold = true; break; }
  }
  return { italic: ITALIC_RE.test(text), bold };
}

/** 이 그림에 심을 얼굴 — 바로 선 글자는 항상, 기울임/굵은 얼굴은 그림이 쓸 때만(그리고 그 얼굴이 있을 때만). faces 는 검사용 주입. */
export function facesFor(svgText, faces = KYMATH_FACES) {
  const use = fontUseOf(svgText);
  return faces.filter((f) => (f.style === 'normal' || use.italic) && (f.weight < 600 || use.bold));
}

const MARK = 'data-figure-font';                                          // 심은 <style> 의 표식 — 벗길 때 이것만 찾는다
const styleBlockFor = (svgText) =>
  `<style ${MARK}="${KYMATH_FAMILY}">${facesFor(svgText).map(faceCss).join('')}${figureFontCss()}</style>`;
const STYLE_RE = new RegExp(`<style ${MARK}="[^"]*">[\\s\\S]*?</style>`, 'g');
const ROOT_RE = /<svg\b[^>]*>/;                                           // 루트 여는 태그(정제된 SVG 라 하나뿐 — readStamp 와 같은 전제)

/** SVG 원문에 글꼴을 심는다. 이미 심어져 있으면 그대로. 루트가 없거나 빈 루트(<svg …/>)면 심을 글자도 없으니 그대로. */
export function embedFigureFont(svgText) {
  if (typeof svgText !== 'string' || svgText.includes(`${MARK}="`)) return svgText;
  const m = ROOT_RE.exec(svgText);
  if (!m || m[0].endsWith('/>')) return svgText;
  const at = m.index + m[0].length;
  return svgText.slice(0, at) + styleBlockFor(svgText) + svgText.slice(at);
}

/** 심은 것을 벗긴다 — 표식 있는 <style> 덩이만 지운다. 속성은 건드린 적이 없으니 원문이 그대로 돌아온다. */
export function stripFigureFont(svgText) {
  if (typeof svgText !== 'string' || !svgText.includes(`${MARK}="`)) return svgText;
  return svgText.replace(STYLE_RE, '');
}

// src → 심은 src. 같은 그림이 목록·미리보기·인쇄에서 여러 번 지나가므로 디코드·인코드를 아낀다. 글자 수 예산을 넘기면 오래된 것부터 버린다.
const cache = new Map();
let cacheChars = 0;
export const CACHE_MAX_CHARS = 8000000;   // ≈ 그림 60~80장(기울임 그림 열쇠+값 ≈ 100~130K 자, 바로 선 글자만이면 ≈ 55K 자)

function remember(key, value) {
  const chars = key.length + value.length;
  while (cache.size > 0 && cacheChars + chars > CACHE_MAX_CHARS) {
    const oldest = cache.keys().next().value;
    cacheChars -= oldest.length + cache.get(oldest).length;
    cache.delete(oldest);
  }
  cache.set(key, value);
  cacheChars += chars;
}

/** data-URI(src) 용 — SVG data-URI 면 글꼴을 심어 새 data-URI 로, 아니면(픽셀 PNG·디코드 실패·undefined) 그대로. */
export function withFigureFont(src) {
  const text = fromDataUri(src);
  if (text === null) return src;
  const hit = cache.get(src);
  if (hit !== undefined) return hit;
  let out;
  try {
    out = toDataUri(embedFigureFont(text));
  } catch (e) {   // 홀로 선 서로게이트 등 — 심지 못하면 원래 src 로(표시는 되던 대로)
    return src;
  }
  remember(src, out);
  return out;
}

/** 심은 data-URI → 심기 전 data-URI. 심은 적이 없거나 SVG 가 아니면 **같은 문자열**을 돌려준다(붙여넣기 경로가 이걸 믿는다). */
export function withoutFigureFont(src) {
  const text = fromDataUri(src);
  if (text === null || !text.includes(`${MARK}="`)) return src;
  try {
    return toDataUri(stripFigureFont(text));
  } catch (e) {
    return src;
  }
}

export const FIGURE_FONT_STYLE_ID = 'figure-font-kymath';

/**
 * 편집기 캔버스(인라인 DOM)용 — 페이지 <head> 에 얼굴 전부의 @font-face 와 규칙 4줄(prefix 접두)을 **한 번만** 건다.
 * SVG DOM 은 건드리지 않으므로 serialize·이력·저장본에 아무것도 섞이지 않는다. 동봉 가이드의 "루트 속성에 넣기"를 쓰지 않은 이유다.
 */
export function ensureFigureFontStyle(prefix, doc = typeof document === 'undefined' ? null : document) {
  if (!doc || doc.getElementById(FIGURE_FONT_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = FIGURE_FONT_STYLE_ID;
  style.textContent = FIGURE_FONT_FACE_CSS + figureFontCss(prefix);
  doc.head.appendChild(style);
}
